import React, { useState } from 'react';

const PRIORITIES = ['Urgent', 'High', 'Medium', 'Low'];

const DUE_OPTIONS = [
  { v: '', label: 'Any due date' },
  { v: 'overdue', label: 'Overdue' },
  { v: 'today', label: 'Due today' },
  { v: 'week', label: 'Next 7 days' },
  { v: 'none', label: 'No due date' },
];

const SORT_OPTIONS = [
  { v: '', label: 'Manual order' },
  { v: 'due-asc', label: 'Due date' },
  { v: 'priority-desc', label: 'Priority' },
  { v: 'created-desc', label: 'Newest' },
  { v: 'title-asc', label: 'Title A-Z' },
];

const toggleValue = (arr, value) =>
  arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];

// A small dropdown that holds a checklist of options for multi-select filters.
const MultiSelect = ({ label, options, selected, onToggle, isOpen, onOpen }) => (
  <div className="relative">
    <button
      type="button"
      onClick={onOpen}
      className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 ${
        selected.length > 0
          ? 'bg-primary-blue text-white border-primary-blue shadow-sm'
          : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800'
      }`}
    >
      {label}
      {selected.length > 0 && (
        <span className="bg-white/30 text-white text-[10px] font-black rounded-full px-1.5 py-0.5 leading-none">
          {selected.length}
        </span>
      )}
      <svg className="w-3 h-3 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="absolute top-full left-0 mt-1 w-56 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-1.5 z-30 scrollbar-thin">
        {options.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic px-2 py-2">None available</p>
        ) : (
          options.map(opt => {
            const isChecked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs transition-colors ${
                  isChecked ? 'bg-primary-blue/10 text-primary-blue font-bold' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${isChecked ? 'bg-primary-blue border-primary-blue' : 'border-gray-300 dark:border-gray-600'}`}>
                  {isChecked && (
                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                {opt.color && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />}
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })
        )}
      </div>
    )}
  </div>
);

const FilterBar = ({
  filters,
  setFilters,
  assigneeOptions = [],
  labelOptions = [],
  tagOptions = [],
  currentUser,
  totalCount = 0,
  filteredCount = 0,
}) => {
  const [openMenu, setOpenMenu] = useState(null); // 'assignee' | 'label' | 'priority' | null

  const activeCount =
    (filters.q ? 1 : 0) +
    filters.assignees.length +
    filters.labels.length +
    (filters.tags || []).length +
    filters.priorities.length +
    (filters.due ? 1 : 0);
  const hasActive = activeCount > 0 || !!filters.sort;

  const update = (patch) => setFilters(prev => ({ ...prev, ...patch }));
  const clearAll = () =>
    setFilters({ q: '', assignees: [], labels: [], tags: [], priorities: [], due: '', sort: '' });

  const assigneeSelectOptions = [
    { value: 'unassigned', label: 'Unassigned' },
    ...assigneeOptions.map(u => ({ value: (u._id || u).toString(), label: u._id === currentUser?._id ? `${u.name} (me)` : (u.name || u.email || 'Unknown') })),
  ];
  const labelSelectOptions = labelOptions.map(l => ({ value: l.text, label: l.text, color: l.color }));
  const tagSelectOptions = tagOptions.map(t => ({ value: t, label: `#${t}` }));
  const prioritySelectOptions = PRIORITIES.map(p => ({ value: p, label: p }));

  const toggleMenu = (name) => setOpenMenu(prev => (prev === name ? null : name));

  return (
    <div className="relative flex-shrink-0 mb-4">
      {/* Click-away backdrop for open dropdowns */}
      {openMenu && <div className="fixed inset-0 z-20" onClick={() => setOpenMenu(null)} />}

      <div className="relative z-20 flex flex-wrap items-center gap-2 bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/50 dark:border-gray-700/50 shadow-sm">
        {/* Search */}
        <div className="relative">
          <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Filter cards..."
            value={filters.q}
            onChange={(e) => update({ q: e.target.value })}
            className="w-44 text-xs font-medium pl-8 pr-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-blue focus:border-primary-blue"
          />
        </div>

        <MultiSelect
          label="Assignee"
          options={assigneeSelectOptions}
          selected={filters.assignees}
          onToggle={(v) => update({ assignees: toggleValue(filters.assignees, v) })}
          isOpen={openMenu === 'assignee'}
          onOpen={() => toggleMenu('assignee')}
        />
        <MultiSelect
          label="Label"
          options={labelSelectOptions}
          selected={filters.labels}
          onToggle={(v) => update({ labels: toggleValue(filters.labels, v) })}
          isOpen={openMenu === 'label'}
          onOpen={() => toggleMenu('label')}
        />
        <MultiSelect
          label="Tag"
          options={tagSelectOptions}
          selected={filters.tags || []}
          onToggle={(v) => update({ tags: toggleValue(filters.tags || [], v) })}
          isOpen={openMenu === 'tag'}
          onOpen={() => toggleMenu('tag')}
        />
        <MultiSelect
          label="Priority"
          options={prioritySelectOptions}
          selected={filters.priorities}
          onToggle={(v) => update({ priorities: toggleValue(filters.priorities, v) })}
          isOpen={openMenu === 'priority'}
          onOpen={() => toggleMenu('priority')}
        />

        {/* Due date (single select) */}
        <select
          value={filters.due}
          onChange={(e) => update({ due: e.target.value })}
          className={`text-xs font-bold rounded-lg border px-2 py-1.5 cursor-pointer transition-colors ${
            filters.due
              ? 'bg-primary-blue text-white border-primary-blue'
              : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
          }`}
        >
          {DUE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>

        {/* Sort */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-black uppercase tracking-wide text-gray-400">Sort</span>
          <select
            value={filters.sort}
            onChange={(e) => update({ sort: e.target.value })}
            className={`text-xs font-bold rounded-lg border px-2 py-1.5 cursor-pointer transition-colors ${
              filters.sort
                ? 'bg-gray-700 text-white border-gray-700 dark:bg-gray-600 dark:border-gray-600'
                : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
            }`}
          >
            {SORT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        {hasActive && (
          <>
            <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1">
              {filteredCount} of {totalCount}
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] font-bold text-red-500 hover:text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Clear all
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
